import {useState} from "react";
import {useSelector} from "react-redux";
import Team from "./team";
import {normalizeMembershipEntity, prepareMembershipPayload} from "../workspace-membership/workspace-membership-identity";

function membershipPopulate() {
    return {populate: {member: {populate: ["profileImage"]}}};
}

export function teamPopulate({nested = true} = {}) {
    const membership = membershipPopulate();
    return {
        workspace: {fields: ["id", "documentId"]},
        members: membership,
        owner: membership,
        archivedBy: membership,
        goals: nested ? {populate: {owner: membership}} : {fields: ["id", "documentId"]},
        projects: nested ? {
            populate: {
                owner: membership,
                tasks: {
                    fields: ["id", "documentId", "title", "taskStatus", "startAt", "dueDate", "doneAt", "archivedAt"],
                    populate: {assignee: membership},
                },
            },
        } : {fields: ["id", "documentId"]},
    };
}

export function useTeam() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const {workspaceMembershipData} = useSelector((state) => state.app);
    const memberships = workspaceMembershipData?.workspace?.workspace_memberships || [];
    const normalizeTeam = (item) => normalizeMembershipEntity(item, "team", memberships);

    const run = async (action) => {
        setLoading(true);
        setError(null);
        try {
            return await action();
        } catch (requestError) {
            setError(requestError);
            throw requestError;
        } finally {
            setLoading(false);
        }
    };

    const getTeams = (workspaceId) => run(async () => {
        if (!workspaceId) return [];
        const response = await new Team().getAll({
            filters: {$and: [{workspace: {$eq: workspaceId}}, {archivedAt: {$null: true}}]},
            populate: teamPopulate({nested: false}),
            sort: ["name:asc"],
            pagination: {pageSize: 100},
        });
        return (response?.data || []).map(normalizeTeam);
    });

    const getTeam = (documentId) => run(async () => {
        const response = await new Team().getById(documentId, {populate: teamPopulate()});
        return normalizeTeam(response?.data);
    });

    const updateTeam = (documentId, teamData) => run(async () => {
        const payload = prepareMembershipPayload("team", teamData, memberships, {
            operation: "update",
            currentMembership: workspaceMembershipData,
        });
        const response = await new Team().update(documentId, payload, {populate: teamPopulate()});
        return normalizeTeam(response?.data);
    });

    const createTeam = (teamData) => run(async () => {
        const payload = prepareMembershipPayload("team", teamData, memberships, {
            operation: "create",
            currentMembership: workspaceMembershipData,
        });
        const response = await new Team().create(payload, {populate: teamPopulate()});
        return normalizeTeam(response?.data);
    });

    const archiveTeam = (documentId) => run(async () => {
        const response = await new Team().archiveItem(documentId);
        return normalizeTeam(response?.data);
    });

    const getTeamSummary = (documentId) => run(async () => (await new Team().getSummary(documentId))?.data || null);
    const getTeamHealth = (documentId) => run(async () => (await new Team().getHealth(documentId))?.data || null);
    const getTeamTasks = (documentId, query = {}) => run(async () => {
        const response = await new Team().getTasks(documentId, query);
        return (response?.data || []).map((item) => normalizeMembershipEntity(item, "task", memberships));
    });
    const updateTeamMember = (documentId, membership, action) => run(async () => {
        const response = await new Team().updateMembers(documentId, {
            membership: membership?.documentId || membership?.id || membership,
            action,
        });
        return normalizeTeam(response?.data);
    });

    // Keep the table contract used by the member profile and older team views.
    // Team API work must not make those existing consumers crash at runtime.
    const prepareTable = () => ({
        fields: ["name"],
        fieldsConfig: {
            name: {alias: "title", type: "TEXT", "class-name": "flex-1 pl-3 pr-3", translate: true},
        },
    });

    return {
        getTeams,
        getTeam,
        updateTeam,
        createTeam,
        archiveTeam,
        getTeamSummary,
        getTeamHealth,
        getTeamTasks,
        updateTeamMember,
        prepareTable,
        loading,
        error,
    };
}
